/**
 * D28 — funzioni pure della pagina «Configurazione Nora» (docs/nora/09 §8–§9).
 *
 * Nessuna dipendenza da React o dai servizi: qui vivono le trasformazioni
 * form → comando e le etichette. La pagina non ricalcola mai `readiness`: la
 * legge dal DTO `overview` del servizio `noraConfiguration`.
 */

/** Id delle tre capabilities agentiche, nell'ordine del catalogo (09 §4). */
export const CAPABILITY_IDS = Object.freeze([
  'weather',
  'trip_planner',
  'travel_policy',
  'web_search'
])

/** Modalità che non crea un contratto agentico. */
export const PROCESS_UNIT = 'process_unit'

/** 09 §3.6 — i canali su cui si sceglie la modalità, nell'ordine del catalogo del server. */
export const CANALI_NORA = Object.freeze(['appNora', 'teams', 'email', 'midoffice', 'whatsapp'])

/** @type {Record<string, string>} */
export const CHIAVI_CANALE = {
  appNora: 'cnCanaleApp',
  teams: 'cnCanaleTeams',
  email: 'cnCanaleEmail',
  midoffice: 'cnCanaleMidoffice',
  whatsapp: 'cnCanaleWhatsapp'
}

/**
 * Le scelte per canale della card: solo i canali che hanno una scelta propria.
 * @param {Object|null} [channelModes] - `channelModes` della card
 * @returns {Record<string, string>} Canale → modalità
 */
export const canaliDaCard = (channelModes) =>
  Object.fromEntries(
    CANALI_NORA.filter((c) => typeof channelModes?.[c] === 'string').map((c) => [
      c,
      channelModes[c]
    ])
  )

/**
 * Vero se almeno un canale userà una modalità agentica: la base o una scelta di canale.
 * Serve solo a spegnere i controlli delle capabilities; la regola vera resta del server.
 * @param {{ mode: string, canali?: Record<string, string> }} form - Form corrente
 * @returns {boolean}
 */
export const usaAgentico = (form) =>
  form?.mode !== PROCESS_UNIT || Object.values(form?.canali || {}).some((m) => m !== PROCESS_UNIT)

/**
 * La pagina mostra due scelte, motore e viaggi; il server tiene una modalità sola.
 * Nora senza viaggi è `assistant`, con i viaggi è la modalità del fornitore.
 */
export const VIAGGI = Object.freeze({
  nessuno: 'assistant',
  dinamico: 'dynamic_demo',
  goelett: 'goelett_booking'
})

/**
 * I viaggi di una modalità: `null` per Process-Unit o per una modalità che la pagina non
 * sa scomporre.
 * @param {string} mode - Modalità del server
 * @returns {'nessuno'|'dinamico'|'goelett'|null}
 */
export const viaggiDi = (mode) =>
  Object.entries(VIAGGI).find(([, modalita]) => modalita === mode)?.[0] ?? null

/**
 * Le scelte di viaggio che questo server offre, nell'ordine della pagina.
 * @param {Array} modes - `overview.modes`
 * @returns {Array<'nessuno'|'dinamico'|'goelett'>}
 */
export const viaggiDisponibili = (modes = []) =>
  Object.keys(VIAGGI).filter((viaggi) => modes.some((m) => m.id === VIAGGI[viaggi]))

/**
 * Le modalità che un canale può scegliere, dal DTO `overview.modes` (`channels` di ogni
 * modalità). L'app non ha Process-Unit fra le sue.
 * @param {string} canale - Canale
 * @param {Array} modes - `overview.modes`
 * @returns {Array} Modalità ammesse
 */
export const modalitaPerCanale = (canale, modes = []) =>
  modes.filter((m) => Array.isArray(m.channels) && m.channels.includes(canale))

/** Percorso obbligatorio in coda all'URL base Goelett (senza, Goelett risponde 404). */
export const PERCORSO_PUBBLICO_GOELETT = '/nora/public'

/**
 * Chiavi di traduzione per modalità: il testo del DTO resta il ripiego se il
 * server aggiunge una modalità che il Web non conosce ancora.
 * @type {Record<string, { label: string, description: string }>}
 */
const CHIAVI_MODALITA = {
  process_unit: { label: 'cnModoProcessUnit', description: 'cnModoProcessUnitDesc' },
  dynamic_demo: { label: 'cnModoDynamicDemo', description: 'cnModoDynamicDemoDesc' },
  midoffice_case: { label: 'cnModoMidofficeCase', description: 'cnModoMidofficeCaseDesc' },
  goelett_booking: { label: 'cnModoGoelettBooking', description: 'cnModoGoelettBookingDesc' },
  assistant: { label: 'cnModoAssistant', description: 'cnModoAssistantDesc' }
}

/** @type {Record<string, { label: string, description: string }>} */
const CHIAVI_CAPABILITY = {
  weather: { label: 'cnCapWeather', description: 'cnCapWeatherDesc' },
  trip_planner: { label: 'cnCapTripPlanner', description: 'cnCapTripPlannerDesc' },
  travel_policy: { label: 'cnCapTravelPolicy', description: 'cnCapTravelPolicyDesc' },
  web_search: { label: 'cnCapWebSearch', description: 'cnCapWebSearchDesc' }
}

/**
 * Capabilities tutte spente.
 * @returns {{ weather: boolean, trip_planner: boolean, travel_policy: boolean, web_search: boolean }}
 */
export const capabilitiesSpente = () => ({
  weather: false,
  trip_planner: false,
  travel_policy: false,
  web_search: false
})

/** D29 — i mezzi che il trip planner può proporre come viaggio intero. */
export const MEZZI_TRIP_PLANNER = Object.freeze(['flight', 'train', 'car'])

/** @type {Record<string, string>} */
export const CHIAVI_MEZZO = {
  flight: 'cnMezzoVolo',
  train: 'cnMezzoTreno',
  car: 'cnMezzoAuto'
}

/**
 * I mezzi del form: `null` = nessuna scelta, valgono i default del deploy.
 *
 * Non si normalizza `null` a «tutti»: sono due cose diverse, e appiattirle scriverebbe
 * una scelta esplicita in ogni salvataggio, anche dove nessuno l'ha fatta.
 * @param {Object|null} [capabilityOptions] - `capabilityOptions` della card
 * @returns {string[]|null} Mezzi scelti, o null
 */
export const mezziDaCard = (capabilityOptions) => {
  const modes = capabilityOptions?.trip_planner?.modes
  if (!Array.isArray(modes) || modes.length === 0) return null
  return MEZZI_TRIP_PLANNER.filter((mezzo) => modes.includes(mezzo))
}

/**
 * Il frammento `capabilityOptions` del comando: omesso quando non c'è scelta o la
 * capability è spenta.
 * @param {{ mode: string, capabilities: Object, mezzi: string[]|null }} form - Form corrente
 * @returns {Object} Frammento di payload
 */
export const conMezzi = (form) =>
  usaAgentico(form) && form.capabilities?.trip_planner && form.mezzi?.length
    ? { capabilityOptions: { trip_planner: { modes: [...form.mezzi] } } }
    : {}

/**
 * Riduce un oggetto capabilities alla forma esatta dei booleani del catalogo.
 * @param {Object} [capabilities] - Capabilities (anche parziali o con chiavi extra)
 * @returns {{ weather: boolean, trip_planner: boolean, travel_policy: boolean, web_search: boolean }}
 */
export const capabilitiesEsatte = (capabilities) =>
  Object.fromEntries(CAPABILITY_IDS.map((id) => [id, capabilities?.[id] === true]))

/**
 * Stato del form a partire da una card (null = nessuna riga ⇒ Process-Unit).
 * @param {Object|null} card - `NoraConfigurationCard` o null
 * @returns {{ mode: string, capabilities: Object }} Stato form
 */
export const formDaCard = (card) => ({
  mode: card?.mode || PROCESS_UNIT,
  canali: canaliDaCard(card?.channelModes),
  capabilities: capabilitiesEsatte(card?.capabilities),
  mezzi: mezziDaCard(card?.capabilityOptions),
  // Acceso se la card non lo dice: è il comportamento di sempre.
  // Spento se la card non lo dice: un giro si fa una tratta alla volta.
  multiTratta: card?.multiLegTrips === true
})

/**
 * Se nessun canale resta agentico, capabilities e mezzi si spengono: il server li
 * rifiuta accesi (09 §3.1, §3.6).
 * @param {Object} form - Form con `mode` e `canali` già aggiornati
 * @returns {Object} Form coerente
 */
const spegniSeProcessUnit = (form) =>
  usaAgentico(form)
    ? { ...form, capabilities: capabilitiesEsatte(form.capabilities), mezzi: form.mezzi ?? null }
    : { ...form, capabilities: capabilitiesSpente(), mezzi: null }

/**
 * Sceglie la modalità di un canale; `null` = il canale usa la modalità predefinita.
 * @param {Object} form - Form corrente
 * @param {string} canale - Canale
 * @param {string|null} mode - Modalità o null
 * @returns {Object} Nuovo form
 */
export const cambiaCanale = (form, canale, mode) => {
  const canali = { ...(form?.canali || {}) }
  if (mode) canali[canale] = mode
  else delete canali[canale]
  return spegniSeProcessUnit({ ...form, canali })
}

/**
 * Cambia la modalità del form: su Process-Unit le capabilities si spengono,
 * perché il server le rifiuta accese (09 §3.1).
 * @param {{ mode: string, capabilities: Object }} form - Form corrente
 * @param {string} mode - Nuova modalità
 * @returns {{ mode: string, capabilities: Object }} Nuovo form
 */
export const cambiaModalita = (form, mode) =>
  // Senza canali agentici le capabilities si spengono, e con loro decade la scelta dei
  // mezzi: il server rifiuta opzioni per una capability spenta.
  spegniSeProcessUnit({ ...form, mode, canali: { ...(form?.canali || {}) } })

/**
 * Vero se due configurazioni scelgono la stessa modalità e le stesse capabilities.
 * @param {{ mode: string, capabilities: Object }} a - Prima configurazione
 * @param {{ mode: string, capabilities: Object }} b - Seconda configurazione
 * @returns {boolean}
 */
export const stessaConfigurazione = (a, b) => {
  const ca = capabilitiesEsatte(a?.capabilities)
  const cb = capabilitiesEsatte(b?.capabilities)
  // I mezzi contano solo dove il trip planner è acceso in entrambe: altrimenti la loro
  // differenza non è salvabile e il pulsante si accenderebbe per niente.
  const ma = ca.trip_planner ? (a?.mezzi ?? null) : null
  const mb = cb.trip_planner ? (b?.mezzi ?? null) : null
  const stessiMezzi =
    ma === null || mb === null
      ? ma === mb
      : ma.length === mb.length &&
        MEZZI_TRIP_PLANNER.every((m) => ma.includes(m) === mb.includes(m))
  const canaliA = a?.canali || {}
  const canaliB = b?.canali || {}
  const stessiCanali = CANALI_NORA.every((c) => (canaliA[c] ?? null) === (canaliB[c] ?? null))
  return (
    (a?.mode || PROCESS_UNIT) === (b?.mode || PROCESS_UNIT) &&
    CAPABILITY_IDS.every((id) => ca[id] === cb[id]) &&
    stessiMezzi &&
    stessiCanali &&
    (a?.multiTratta === true) === (b?.multiTratta === true)
  )
}

/**
 * Stabilisce se il pulsante «Salva e applica» è attivo.
 * - default environment: serve una differenza rispetto alla card (assenza = Process-Unit);
 * - community senza override: salvare crea l'eccezione, quindi è sempre ammesso;
 * - community con override: serve una differenza.
 * @param {Object} args - Argomenti
 * @param {Object|null} args.card - Card mostrata (null se non esiste)
 * @param {{ mode: string, capabilities: Object }} args.form - Form corrente
 * @param {boolean} args.perCommunity - Vero se il form è un override community
 * @returns {boolean}
 */
export const puoSalvare = ({ card, form, perCommunity }) => {
  if (perCommunity && !card) return true
  return !stessaConfigurazione(formDaCard(card), form)
}

/**
 * Normalizza il motivo facoltativo: stringa vuota ⇒ campo omesso.
 * @param {string} [reason] - Motivo digitato
 * @returns {{ reason?: string }} Frammento di payload
 */
const conMotivo = (reason) =>
  typeof reason === 'string' && reason.trim() !== '' ? { reason: reason.trim() } : {}

/**
 * Comando `saveConfiguration`: soli campi ammessi dal server.
 * @param {Object} args - Argomenti
 * @param {number} args.environmentId - Environment
 * @param {number|null} args.communityPresetId - null = default environment
 * @param {Object|null} args.card - Card mostrata (per `expectedRevision`)
 * @param {{ mode: string, capabilities: Object }} args.form - Scelta dell'utente
 * @param {string} [args.reason] - Motivo facoltativo
 * @returns {Object} Payload del comando
 */
export const payloadSalvataggio = ({ environmentId, communityPresetId, card, form, reason }) => ({
  action: 'saveConfiguration',
  environmentId,
  communityPresetId: communityPresetId ?? null,
  expectedRevision: card?.revision ?? null,
  mode: form.mode,
  ...(Object.keys(form.canali || {}).length ? { channelModes: { ...form.canali } } : {}),
  capabilities: usaAgentico(form) ? capabilitiesEsatte(form.capabilities) : capabilitiesSpente(),
  ...conMezzi(form),
  ...(usaAgentico(form) ? { multiLegTrips: form.multiTratta === true } : {}),
  ...conMotivo(reason)
})

/**
 * Comando `removeOverride` («Eredita dall'environment»): elimina la riga, non ne crea una copia.
 * @param {Object} args - Argomenti
 * @param {number} args.environmentId - Environment
 * @param {number} args.communityPresetId - Preset della community
 * @param {Object} args.override - Card override mostrata
 * @param {string} [args.reason] - Motivo facoltativo
 * @returns {Object} Payload del comando
 */
export const payloadRimozioneOverride = ({
  environmentId,
  communityPresetId,
  override,
  reason
}) => ({
  action: 'removeOverride',
  environmentId,
  communityPresetId,
  expectedRevision: override.revision,
  ...conMotivo(reason)
})

/**
 * Il form Goelett precompilato col collegamento salvato: la pagina lo mostra sempre.
 * Il server restituisce URL, scope, client id e nome del riferimento al segreto, mai il segreto.
 * @param {Object} [stato] - `providerStatus.goelett`
 * @returns {Object} Form Goelett
 */
export const formGoelett = (stato) => ({
  label: stato?.destination === 'production' ? 'production' : 'sandbox',
  baseUrl: stato?.baseUrl || '',
  stsUrl: stato?.stsUrl || '',
  oauthScope: stato?.oauthScope || '',
  clientId: stato?.clientId || '',
  clientSecretRef: stato?.clientSecretRef || ''
})

/**
 * Validazione client del form Goelett (la stessa regola resta autorevole sul server).
 * @param {Object} form - Form Goelett
 * @returns {string[]} Nomi dei campi non validi
 */
export const erroriGoelett = (form = {}) => {
  const errori = []
  const baseUrl = (form.baseUrl || '').trim()
  const stsUrl = (form.stsUrl || '').trim()
  if (!/^https:\/\/\S+$/i.test(baseUrl) || !baseUrl.endsWith(PERCORSO_PUBBLICO_GOELETT)) {
    errori.push('baseUrl')
  }
  if (!/^https:\/\/\S+$/i.test(stsUrl)) errori.push('stsUrl')
  if ((form.clientId || '').trim() === '') errori.push('clientId')
  if ((form.clientSecretRef || '').trim() === '') errori.push('clientSecretRef')
  if (!['sandbox', 'production'].includes(form.label)) errori.push('label')
  return errori
}

/**
 * Comando `saveGoelettConnection`: campi tipizzati, `oauthScope` omesso se vuoto.
 * @param {Object} args - Argomenti
 * @param {number} args.environmentId - Environment
 * @param {Object} [args.stato] - `providerStatus.goelett` (per `expectedRevision`)
 * @param {Object} args.form - Form Goelett
 * @param {string} [args.reason] - Motivo facoltativo
 * @returns {Object} Payload del comando
 */
export const payloadConnessioneGoelett = ({ environmentId, stato, form, reason }) => {
  const scope = (form.oauthScope || '').trim()
  return {
    action: 'saveGoelettConnection',
    environmentId,
    expectedRevision: stato?.configured ? (stato.revision ?? null) : null,
    label: form.label,
    baseUrl: form.baseUrl.trim(),
    stsUrl: form.stsUrl.trim(),
    ...(scope ? { oauthScope: scope } : {}),
    clientId: form.clientId.trim(),
    clientSecretRef: form.clientSecretRef.trim(),
    ...conMotivo(reason)
  }
}

/**
 * Comando `revokeConnection`: il server lo ammette solo per Goelett.
 * @param {Object} args - Argomenti
 * @param {number} args.environmentId - Environment
 * @param {Object} args.stato - `providerStatus.goelett`
 * @param {string} [args.reason] - Motivo facoltativo
 * @returns {Object} Payload del comando
 */
export const payloadRevocaGoelett = ({ environmentId, stato, reason }) => ({
  action: 'revokeConnection',
  environmentId,
  integrationId: 'goelett',
  expectedRevision: stato.revision,
  ...conMotivo(reason)
})

/**
 * Vero se l'errore Feathers è un conflitto CAS (HTTP 409).
 * @param {any} error - Errore del client Feathers
 * @returns {boolean}
 */
export const eConflitto = (error) =>
  error?.code === 409 || error?.name === 'Conflict' || error?.className === 'conflict'

/**
 * Sostituisce i segnaposto `{nome}` di un testo tradotto.
 * @param {string} testo - Testo con segnaposto
 * @param {Record<string, string>} valori - Valori
 * @returns {string}
 */
export const formatta = (testo, valori = {}) =>
  String(testo || '').replace(/\{(\w+)\}/g, (intero, nome) =>
    valori[nome] !== undefined && valori[nome] !== null ? String(valori[nome]) : intero
  )

/**
 * Etichetta e descrizione di una modalità: traduzione se nota, altrimenti testo del DTO.
 * @param {string} modeId - Id modalità
 * @param {Array} modes - `overview.modes`
 * @param {Object} t - Dizionario traduzioni
 * @returns {{ label: string, description: string }}
 */
export const testoModalita = (modeId, modes = [], t = {}) => {
  const chiavi = CHIAVI_MODALITA[modeId]
  const dto = modes.find((m) => m.id === modeId)
  return {
    label: (chiavi && t[chiavi.label]) || dto?.label || modeId,
    description: (chiavi && t[chiavi.description]) || dto?.description || ''
  }
}

/**
 * Etichetta e descrizione di una capability: traduzione se nota, altrimenti testo del DTO.
 * @param {string} capabilityId - Id capability
 * @param {Array} capabilities - `overview.capabilities`
 * @param {Object} t - Dizionario traduzioni
 * @returns {{ label: string, description: string }}
 */
export const testoCapability = (capabilityId, capabilities = [], t = {}) => {
  const chiavi = CHIAVI_CAPABILITY[capabilityId]
  const dto = capabilities.find((c) => c.id === capabilityId)
  return {
    label: (chiavi && t[chiavi.label]) || dto?.label || capabilityId,
    description: (chiavi && t[chiavi.description]) || dto?.description || ''
  }
}

/**
 * Resa dello stato calcolato dal server (09 §9.4). Il Web non lo ricalcola.
 * @param {string} readiness - `card.readiness`
 * @param {Object} t - Dizionario traduzioni
 * @returns {{ label: string, badge: string }} Testo e classe badge
 */
export const statoCard = (readiness, t = {}) => {
  switch (readiness) {
    case 'ready':
      return { label: t.cnStatoPronta, badge: 'nbadge--ok' }
    case 'ready_missing_capabilities':
      return { label: t.cnStatoProntaMancanti, badge: 'nbadge--warn' }
    case 'suspended':
      return { label: t.cnStatoSospesa, badge: 'nbadge--warn' }
    case 'effects_stopped':
      return { label: t.cnStatoEffettiArrestati, badge: 'nbadge--err' }
    case 'not_ready':
    default:
      return { label: t.cnStatoNonPronta, badge: 'nbadge--err' }
  }
}

/**
 * Motivo per cui una community non può avere un'eccezione (09 §1.6).
 * @param {string} [reason] - `cannotOverrideReason`
 * @param {Object} t - Dizionario traduzioni
 * @returns {string}
 */
export const motivoNonOverride = (reason, t = {}) => {
  switch (reason) {
    case 'default_preset':
      return t.cnMotivoDefaultPreset
    case 'inactive':
      return t.cnMotivoInattiva
    case 'no_customer_code':
      return t.cnMotivoSenzaCodice
    default:
      return t.cnMotivoNonAmmessa
  }
}

/**
 * Situazione di una community rispetto al default environment:
 * - `non_ammessa`: nessun comando di override;
 * - `eredita`: nessuna riga override;
 * - `fissata`: override uguale al default corrente;
 * - `eccezione`: override diverso dal default.
 * @param {Object} community - Voce di `overview.communities`
 * @param {Object|null} defaultConfiguration - Card default (null = Process-Unit)
 * @returns {'non_ammessa'|'eredita'|'fissata'|'eccezione'}
 */
export const situazioneCommunity = (community, defaultConfiguration) => {
  if (!community?.canOverride) return 'non_ammessa'
  if (!community.override) return 'eredita'
  return stessaConfigurazione(community.override, formDaCard(defaultConfiguration))
    ? 'fissata'
    : 'eccezione'
}

/**
 * Traduce una destinazione tecnica.
 * @param {string} [destinazione] - 'Test' | 'Production' | 'sandbox' | 'production'
 * @param {Object} t - Dizionario traduzioni
 * @returns {string}
 */
export const testoDestinazione = (destinazione, t = {}) => {
  switch (destinazione) {
    case 'Test':
      return t.cnDestinazioneTest
    case 'Production':
    case 'production':
      return t.cnDestinazioneProduzione
    case 'sandbox':
      return t.cnDestinazioneSandbox
    default:
      return t.cnDestinazioneNonConfigurata
  }
}

/**
 * Sistema esterno e destinazione da nominare nella conferma di una modalità che scrive.
 * Demo Dinamico: destinazione dall'environment; Goelett: dalla connessione salvata.
 * @param {string} modeId - Modalità scelta
 * @param {Object} overview - DTO overview
 * @param {Object} t - Dizionario traduzioni
 * @returns {{ sistema: string, destinazione: string }|null} null se la modalità non scrive
 */
export const scrittureEsterne = (modeId, overview, t = {}) => {
  const modalita = (overview?.modes || []).find((m) => m.id === modeId)
  if (!modalita?.writesExternalSystems) return null
  if (modeId === 'goelett_booking') {
    const goelett = overview?.providerStatus?.goelett
    return {
      sistema: t.cnSistemaGoelett,
      destinazione: testoDestinazione(goelett?.configured ? goelett.destination : undefined, t)
    }
  }
  return {
    sistema: t.cnSistemaMidoffice,
    destinazione: testoDestinazione(overview?.environment?.dynamicTarget, t)
  }
}

/**
 * Stato leggibile di una connessione provider.
 * @param {Object} [stato] - Voce di `providerStatus`
 * @param {Object} t - Dizionario traduzioni
 * @param {boolean} [necessaria=true] - Falso se nessuna configurazione salvata la usa
 * @returns {{ label: string, badge: string }}
 */
export const statoConnessione = (stato, t = {}, necessaria = true) => {
  // Una connessione che nessuna configurazione usa non è un problema: niente giallo.
  if (!stato?.configured && !necessaria) return { label: t.cnConnessioneNonUsata, badge: '' }
  if (!stato?.configured) return { label: t.cnConnessioneAssente, badge: 'nbadge--warn' }
  if (stato.status === 'revoked') return { label: t.cnConnessioneRevocata, badge: 'nbadge--err' }
  return { label: t.cnConnessioneAttiva, badge: 'nbadge--ok' }
}

/**
 * Gli environment che l'utente può scegliere nella pagina.
 * L'admin li gestisce tutti (il server lo ammette); gli altri
 * ruoli vedono solo quelli del proprio codice Tomcat.
 * @param {Array<Object>} environments - Righe `environments`
 * @param {Object} [utente] - `user.user` di `useAuth`
 * @returns {Array<{value: number, label: string}>} Opzioni del selettore
 */
export const environmentSelezionabili = (environments = [], utente) => {
  const tomcatCode = utente?.environment?.tomcatCode
  return environments
    .filter((env) => utente?.role === 'admin' || !tomcatCode || env.tomcatCode === tomcatCode)
    .map(({ id, codSociety, societyName }) => ({
      value: id,
      label: societyName ? `${societyName} (${codSociety})` : codSociety
    }))
}

/** La sola modalità che passa da Goelett; la demo Dinamico ha il proprio collegamento. */
export const MODALITA_GOELETT = 'goelett_booking'

/**
 * Le modalità di un form o di una card: la base più le scelte per canale.
 * @param {{ mode?: string, canali?: Record<string, string>, channelModes?: Object }} [origine]
 * @returns {string[]}
 */
const modalitaDi = (origine) => [
  origine?.mode || PROCESS_UNIT,
  ...Object.values(origine?.canali || canaliDaCard(origine?.channelModes))
]

/**
 * Le modalità usate dalle configurazioni **salvate** dell'environment: default e
 * eccezioni delle community, base e canali.
 * @param {Object} overview - DTO overview
 * @returns {Set<string>}
 */
export const modalitaSalvate = (overview) =>
  new Set(
    [overview?.defaultConfiguration, ...(overview?.communities || []).map((c) => c.override)]
      .filter(Boolean)
      .flatMap(modalitaDi)
  )

/**
 * Vero se il form sceglie Prenotazione Goelett come base o su un canale.
 * @param {Object} form - Form corrente
 * @returns {boolean}
 */
export const formUsaGoelett = (form) => modalitaDi(form).includes(MODALITA_GOELETT)

/**
 * Vero se il collegamento Goelett è salvato e attivo: il server rifiuta Prenotazione
 * Goelett senza (`verificaPrerequisiti`).
 * @param {Object} overview - DTO overview
 * @returns {boolean}
 */
export const goelettAttivo = (overview) => {
  const stato = overview?.providerStatus?.goelett
  return Boolean(stato?.configured && stato.status === 'active')
}

/**
 * Il collegamento Goelett merita la card aperta: una configurazione salvata usa Goelett,
 * oppure il collegamento esiste già (va tenuto d'occhio o revocato).
 * @param {Object} overview - DTO overview
 * @returns {boolean}
 */
export const goelettRilevante = (overview) =>
  modalitaSalvate(overview).has(MODALITA_GOELETT) ||
  Boolean(overview?.providerStatus?.goelett?.configured)

/**
 * Vero se una configurazione salvata usa la demo Dinamico (ogni agentica non Goelett).
 * @param {Object} overview - DTO overview
 * @returns {boolean}
 */
export const midofficeInUso = (overview) =>
  [...modalitaSalvate(overview)].some((m) => m !== PROCESS_UNIT && m !== MODALITA_GOELETT)

/**
 * Le community divise per ciò che la pagina ne può fare.
 * @param {Object} overview - DTO overview
 * @returns {{ modificabili: Array, bloccate: Array, conEccezione: number }}
 */
export const gruppiCommunity = (overview) => {
  const communities = overview?.communities || []
  return {
    // Un override rimasto su una community non più ammessa resta visibile: va rimosso.
    modificabili: communities.filter((c) => c.canOverride || c.override),
    bloccate: communities.filter((c) => !c.canOverride && !c.override),
    conEccezione: communities.filter((c) => c.override).length
  }
}
